import { useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import moment from 'moment';

import { useTranslation } from '../../hooks/useTranslation';

import {
  GridColumn,
  GridRecord,
  GridGetterFunction,
  GridCountFunction,
} from './GridDataTypes';

interface UseGridExportParams {
  columns: GridColumn[];
  filterText: string;
  filterColumns: Record<string, string | undefined>;
  orderBy: string | undefined;
  orderDirection: 'ASC' | 'DESC';
  getData: GridGetterFunction | undefined;
  getDataCount: GridCountFunction | undefined;
  dataWithoutPaging: GridRecord[] | undefined;
  additionalId: string | undefined;
}

const serializeFilters = (
  filterColumns: Record<string, string | undefined>,
) => {
  const filters = _(filterColumns)
    .omitBy(v => v === '')
    .value();
  return Object.keys(filters).length ? JSON.stringify(filters) : undefined;
};

const fetchAllData = async (
  getData: GridGetterFunction | undefined,
  getDataCount: GridCountFunction | undefined,
  dataWithoutPaging: GridRecord[] | undefined,
  filterText: string,
  serializedFilters: string | undefined,
  orderBy: string | undefined,
  orderDirection: 'ASC' | 'DESC',
  additionalId: string | undefined,
): Promise<GridRecord[]> => {
  if (!getData) {
    return dataWithoutPaging || [];
  }
  const totalRows = getDataCount
    ? await getDataCount(filterText, serializedFilters, additionalId)
    : 0;
  return getData(
    Math.max(totalRows, 1),
    1,
    orderBy,
    orderDirection,
    filterText,
    serializedFilters,
    additionalId,
  );
};

const parseNumericString = (value: string) => {
  const parsed = parseFloat(value.replace(/[^\d.,-]/g, '').replace(',', '.'));
  return isNaN(parsed) ? value : parsed;
};

const extractTextFromReactElement = (element: unknown): string => {
  if (element === null || element === undefined) {
    return '';
  }
  if (typeof element === 'string') {
    return element;
  }
  if (typeof element === 'number') {
    return String(element);
  }
  if (Array.isArray(element)) {
    return element.map(extractTextFromReactElement).join('');
  }
  if (typeof element === 'object' && 'props' in (element as object)) {
    const el = element as { props?: { children?: unknown } };
    return extractTextFromReactElement(el.props?.children);
  }
  return '';
};

const getCellValue = (
  row: GridRecord,
  col: GridColumn,
): string | number | Date | undefined => {
  const exportVal = row.exportValues?.[col.property];
  if (exportVal !== undefined) {
    return col.exportType === 'number' && typeof exportVal === 'string'
      ? parseNumericString(exportVal)
      : (exportVal as string | number | Date);
  }

  const numVal = row.orderByNumberValues?.[col.property];
  if (numVal !== undefined) {
    return numVal;
  }

  const strVal = row.orderByStringValues?.[col.property];
  if (strVal !== undefined) {
    if (col.exportType === 'number') {
      return parseNumericString(strVal);
    }
    if (col.exportType === 'date') {
      const parsed = moment(strVal, ['YYYY-MM-DD', 'YYYY-MM-DD'], true);
      return parsed.isValid() ? parsed.toDate() : strVal;
    }
    return strVal;
  }

  const raw = row.values[col.property];
  if (typeof raw === 'string') {
    if (col.exportType === 'number') {
      return parseNumericString(raw);
    }
    if (col.exportType === 'date') {
      const parsed = moment(raw, ['YYYY-MM-DD', 'YYYY-MM-DD'], true);
      return parsed.isValid() ? parsed.toDate() : raw;
    }
    return raw;
  }
  return typeof raw === 'number' ? raw : extractTextFromReactElement(raw);
};

const buildActiveColumn = (
  allData: GridRecord[],
  activeLabel: string,
  yesLabel: string,
  noLabel: string,
) => {
  const hasInactiveColumn = allData.some(row => row.isInactive !== undefined);
  if (!hasInactiveColumn) {
    return { header: null, getValue: () => null };
  }
  return {
    header: activeLabel,
    getValue: (row: GridRecord) => (row.isInactive ? noLabel : yesLabel),
  };
};

const MAX_COL_WIDTH = 60;

const estimateFormattedNumberWidth = (n: number): number => {
  const intPart = Math.abs(Math.trunc(n)).toString();
  const separators = Math.floor((intPart.length - 1) / 3);
  const sign = n < 0 ? 1 : 0;
  return intPart.length + separators + sign + 3; // +3 for ",00"
};

const calcColumnWidths = (
  headers: string[],
  rows: (string | number | Date | undefined | null)[][],
): number[] => {
  const widths = headers.map(h => h.length);
  for (const row of rows) {
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      const len =
        val instanceof Date
          ? 10 // yyyy-mm-dd
          : typeof val === 'number'
            ? estimateFormattedNumberWidth(val)
            : String(val ?? '').length;
      if (len > (widths[c] ?? 0)) {
        widths[c] = len;
      }
    }
  }
  return widths.map(w => Math.min(w, MAX_COL_WIDTH));
};

const applyWorksheetFormats = (
  ws: XLSX.WorkSheet,
  columns: GridColumn[],
  rowCount: number,
  headers: string[],
  rows: (string | number | Date | undefined | null)[][],
) => {
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rowCount, c: headers.length - 1 },
    }),
  };
  ws['!cols'] = calcColumnWidths(headers, rows).map(w => ({ wch: w }));
  for (let c = 0; c < headers.length; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) {
      cell.s = { font: { bold: true, sz: 12 } };
    }
  }
  for (let c = 0; c < headers.length; c++) {
    for (let r = 1; r <= rowCount; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) {
        continue;
      }
      cell.s = { font: { sz: 12 } };
      if (cell.t === 'd') {
        cell.z = 'yyyy-mm-dd';
      } else if (columns[c]?.exportType === 'number') {
        cell.z = '#,##0.00';
      }
    }
  }
};

export const useGridExport = ({
  columns,
  filterText,
  filterColumns,
  orderBy,
  orderDirection,
  getData,
  getDataCount,
  dataWithoutPaging,
  additionalId,
}: UseGridExportParams) => {
  const { t, tk } = useTranslation('commonLng');
  return useCallback(async () => {
    const serializedFilters = serializeFilters(filterColumns);
    const allData = await fetchAllData(
      getData,
      getDataCount,
      dataWithoutPaging,
      filterText,
      serializedFilters,
      orderBy,
      orderDirection,
      additionalId,
    );

    const activeColumn = buildActiveColumn(
      allData,
      t(tk.component.activeAutomatic),
      t(tk.common.yesUpperCase),
      t(tk.common.noUpperCase),
    );
    const headers = [
      ...columns.map(c => c.header),
      ...(activeColumn.header ? [activeColumn.header] : []),
    ];
    const rows = allData.map(row => {
      const activeValue = activeColumn.getValue(row);
      return [
        ...columns.map(col => getCellValue(row, col)),
        ...(activeValue !== null ? [activeValue] : []),
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows], {
      cellDates: true,
    });
    applyWorksheetFormats(ws, columns, allData.length, headers, rows);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dane');
    const buf = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
      cellStyles: true,
    });
    const filename = `${t(tk.component.exportFileName)}_${moment().format('YYYY-MM-DD')}.xlsx`;
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
  }, [
    columns,
    filterText,
    filterColumns,
    orderBy,
    orderDirection,
    getData,
    getDataCount,
    dataWithoutPaging,
    additionalId,
    t,
    tk,
  ]);
};
